import {
  ACCOUNT_FIELDS_MAX_LENGTH,
  LEAD_CUSTOM_FIELDS,
  ROLES,
  SSO_STRATEGIES,
  TRIALSTATUS,
} from '../constants';
import { Idp, idpStrategies, supportedStrategies } from '../constants/Idp';
import {
  Account,
  LeadAPI,
  StrategyData,
  StrategyName,
  StrategyOptions,
  Theme,
  ThemeDomain,
  ThemeIdentityProvider,
  ThemeSSO,
} from '../types/api';
import { Unit } from '../types/api/unit.type';
import { AccountFieldsErr, AccountImportData, LeadCustomFieldType } from '../types/global';
import { nonNullable } from '../types/util';

export const emailRegexp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const urlRegexp =
  /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d+)?(\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?$/;
export const phoneRegexp = /^\+?\s*(\(\d{1,4}\)|\d{1,4})\s*([().\-/\s]*[0-9]{1,4}[().-/\s]*){1,5}$/;

export const validateEmail = (value: string): boolean => {
  // SOURCE: https://community.auth0.com/t/email-validation-rules-please-make-them-public-knowledge/29532
  // SAME regExp THAT AUTH0 USES

  const result = emailRegexp.exec(value.toLowerCase());
  return !!result && result[0] !== null;
};

export const validateUrl = (value: string): boolean => {
  return urlRegexp.test(value.toLowerCase());
};

export const validatePhone = (value: string): boolean => {
  return phoneRegexp.test(value.toLowerCase());
};

/*
  Auth0 fields rules:
  __email__ The maximum length is 64 characters for the user/local part and 256 characters for the domain part.
  https://auth0.com/docs/manage-users/user-accounts/create-users
  first
  __names__ The maximum length is 150 chars for each first & last name property.
   https://community.auth0.com/t/firstname-lastname-middlename-chracter-length/66157/5
*/

export const checkAccountFieldsLength = (acc: AccountImportData): AccountFieldsErr => {
  const err: AccountFieldsErr = {};
  const email_user = acc.email?.split('@').shift();
  const email_domain = acc.email?.split('@').pop();

  if (
    !!email_user &&
    !!email_domain &&
    (email_user.length > ACCOUNT_FIELDS_MAX_LENGTH.EMAILUSER ||
      email_domain.length > ACCOUNT_FIELDS_MAX_LENGTH.EMAILDOMAIN)
  )
    err.email = true;

  if (!!acc.workEmail && acc.workEmail.length > ACCOUNT_FIELDS_MAX_LENGTH.EMAIL)
    err.workEmail = true;

  if (!!acc.workEmail2 && acc.workEmail2.length > ACCOUNT_FIELDS_MAX_LENGTH.EMAIL)
    err.workEmail2 = true;

  if (!!acc.id && acc.id.length > ACCOUNT_FIELDS_MAX_LENGTH.ID) err.id = true;

  if (!!acc.firstName && acc.firstName.length > ACCOUNT_FIELDS_MAX_LENGTH.FIRSTNAME)
    err.firstName = true;

  if (!!acc.lastName && acc.lastName.length > ACCOUNT_FIELDS_MAX_LENGTH.LASTNAME)
    err.lastName = true;

  if (!!acc.username && acc.username.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT) err.username = true;

  if (!!acc.position && acc.position.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT) err.position = true;

  if (!!acc.role && acc.role.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT) err.role = true;

  if (!!acc.division && acc.division.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT) err.division = true;

  if (!!acc.company && acc.company.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT) err.company = true;

  if (!!acc.mobilePhone && acc.mobilePhone.length > ACCOUNT_FIELDS_MAX_LENGTH.NUMBER)
    err.mobilePhone = true;

  if (!!acc.workPhone && acc.workPhone.length > ACCOUNT_FIELDS_MAX_LENGTH.NUMBER)
    err.workPhone = true;

  if (!!acc.homePhone && acc.homePhone.length > ACCOUNT_FIELDS_MAX_LENGTH.NUMBER)
    err.homePhone = true;

  if (!!acc.hotlinePhone && acc.hotlinePhone.length > ACCOUNT_FIELDS_MAX_LENGTH.NUMBER)
    err.hotlinePhone = true;

  if (!!acc.assistantPhone && acc.assistantPhone.length > ACCOUNT_FIELDS_MAX_LENGTH.NUMBER)
    err.assistantPhone = true;

  if (!!acc.workFax && acc.workFax.length > ACCOUNT_FIELDS_MAX_LENGTH.NUMBER) err.workFax = true;

  if (!!acc.address1_addressee && acc.address1_addressee.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT)
    err.address1_addressee = true;
  if (
    !!acc.address1_addressLine1 &&
    acc.address1_addressLine1.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT
  )
    err.address1_addressLine1 = true;
  if (
    !!acc.address1_addressLine2 &&
    acc.address1_addressLine2.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT
  )
    err.address1_addressLine2 = true;
  if (!!acc.address1_postCode && acc.address1_postCode.length > ACCOUNT_FIELDS_MAX_LENGTH.NUMBER)
    err.address1_postCode = true;
  if (!!acc.address1_city && acc.address1_city.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT)
    err.address1_city = true;
  if (!!acc.address1_country && acc.address1_country.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT)
    err.address1_country = true;
  if (!!acc.address1_label && acc.address1_label.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT)
    err.address1_label = true;

  if (!!acc.address2_addressee && acc.address2_addressee.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT)
    err.address2_addressee = true;
  if (
    !!acc.address2_addressLine1 &&
    acc.address2_addressLine1.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT
  )
    err.address2_addressLine1 = true;
  if (
    !!acc.address2_addressLine2 &&
    acc.address2_addressLine2.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT
  )
    err.address2_addressLine2 = true;
  if (!!acc.address2_postCode && acc.address2_postCode.length > ACCOUNT_FIELDS_MAX_LENGTH.NUMBER)
    err.address2_postCode = true;
  if (!!acc.address2_city && acc.address2_city.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT)
    err.address2_city = true;
  if (!!acc.address2_country && acc.address2_country.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT)
    err.address2_country = true;
  if (!!acc.address2_label && acc.address2_label.length > ACCOUNT_FIELDS_MAX_LENGTH.TEXT)
    err.address2_label = true;

  if (
    !!acc.addressTemplate1Id &&
    (acc.addressTemplate1Id > ACCOUNT_FIELDS_MAX_LENGTH.INTEGER ||
      acc.addressTemplate1Id < -ACCOUNT_FIELDS_MAX_LENGTH.INTEGER)
  )
    err.addressTemplate1Id = true;

  if (
    !!acc.addressTemplate2Id &&
    (acc.addressTemplate2Id > ACCOUNT_FIELDS_MAX_LENGTH.INTEGER ||
      acc.addressTemplate2Id < -ACCOUNT_FIELDS_MAX_LENGTH.INTEGER)
  )
    err.addressTemplate2Id = true;

  if (!!acc.workUrl && acc.workUrl.length > ACCOUNT_FIELDS_MAX_LENGTH.URL) err.workUrl = true;

  Object.keys(err).length > 0 && (err.user = acc.email);

  return err;
};

export const isInFuture = (date?: Date | string | null): boolean =>
  !!(date && new Date(date).getTime() > new Date().getTime());

export const getContactFormTranslationKeyByField = (field: string): string => {
  if (field.toLowerCase().includes('phone')) return `${field}.text`;

  if (field === 'gdprLegalBasis') return `gdpr`;

  return field;
};

/**
 * alternative to String.prototype.replaceAll native helper which not supported in old browsers
 * @param str  the string to replace inside
 * @param find the string to be replaced
 * @param replace the string to be replaced with
 * @returns replaced string
 */
export const replaceAll = (str: string, find: string, replace: string) =>
  str.replace(new RegExp(escapeRegExp(find), 'g'), replace);

/**
 * escape string from regular expression special charachters
 * @param string
 * @returns escaped string
 */
const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

export const getMainUnitSync = (account: Account): null | Unit =>
  account.accountToUnits?.find(atu => atu.isMain)?.unit || null;
export const getOtherUnitsSync = (account: Account): Array<Unit> =>
  account.accountToUnits
    ?.filter(atu => !atu.isMain)
    ?.map(atu => atu.unit)
    .filter(nonNullable) || [];
/*
 *
 * @param strategyData
 * @returns array of missed required fields
 */
export const checkRequiredStrategyFields = (
  strategyData: StrategyData,
): Array<keyof StrategyData | keyof StrategyOptions> => {
  const missed_fields: Array<keyof StrategyData | keyof StrategyOptions> = [];

  const strategy_options = strategyData.options;

  !strategyData.strategy && missed_fields.push('strategy');
  !strategyData.connectionName && missed_fields.push('connectionName');
  !strategy_options && missed_fields.push('options');

  if (!!strategyData.strategy && !!strategy_options) {
    switch (strategyData.strategy) {
      case SSO_STRATEGIES.SAMLP:
        // !strategy_options.certificate && missed_fields.push('certificate');
        // !strategy_options.signInUrl && missed_fields.push('signInUrl');
        // !strategy_options.metaDataUrl && missed_fields.push('metaDataUrl');
        break;
      case SSO_STRATEGIES.OKTA:
        !strategy_options.oktaDomain && missed_fields.push('oktaDomain');
        !strategy_options.clientID && missed_fields.push('clientID');
        !strategy_options.clientSecret && missed_fields.push('clientSecret');
        break;
      case SSO_STRATEGIES.ENTRA_ID:
        !strategy_options.entraIdDomain && missed_fields.push('entraIdDomain');
        !strategy_options.clientID && missed_fields.push('clientID');
        !strategy_options.clientSecret && missed_fields.push('clientSecret');
        break;
      case SSO_STRATEGIES.OIDC:
        !strategy_options.metadata && missed_fields.push('metadata');
        !strategy_options.clientID && missed_fields.push('clientID');
        !strategy_options.clientSecret && missed_fields.push('clientSecret');
        break;
      default:
        break;
    }
  }

  return missed_fields;
};

export const getSSOStrategies = (themeIdp: ThemeIdentityProvider): StrategyName[] => {
  const strategies: readonly StrategyName[] = idpStrategies[themeIdp.name as Idp] || ['samlp'];
  return strategies as StrategyName[];
};

export const isSupportedSsoStrategy = (strategy?: string) =>
  strategy && supportedStrategies.includes(strategy);

export const normalizeThemeName = (str: string): string => str.replace(/\s+/g, '-');

export const getSSODomains = (ssoConnections?: ThemeSSO[]) => {
  return (
    ssoConnections?.reduce((prev, { domains }) => [...prev, ...domains], [] as ThemeDomain[]) || []
  );
};

export const getIdpSettingsForTheme = (theme?: Theme) => {
  const idps = theme?.identityProviders || [];
  const ssoConnections = idps.map(x => x.themeSSO).filter(x => x?.enabled) as unknown as ThemeSSO[];
  const ssoDomains = getSSODomains(ssoConnections);

  return { idps, ssoConnections, ssoDomains };
};

export const themeHasSSO = (theme: Theme) => {
  const { ssoConnections } = getIdpSettingsForTheme(theme);
  return ssoConnections.length > 0;
};

export const isSSOUserEmail = (theme: Theme, authEmail?: string | null) => {
  const { ssoConnections, ssoDomains } = getIdpSettingsForTheme(theme);

  if (ssoConnections.length === 0) return false;

  const emailDomain = authEmail?.split('@').pop()?.toLowerCase();
  if (!emailDomain) return false;
  const domains = ssoDomains.map(x => x.domain);

  return domains.includes(emailDomain);
};

export const appendParamsToLink = (link: string, params: Record<string, string | number>) => {
  const url = new URL(link);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value.toString());
  });
  return url.toString();
};

export const CLAIMS = {
  ID_TOKEN: {
    ROLES: 'https://lemontaps.com/claim/roles',
  },
};
export const hasRole = (idTokenClaims: Record<string, any>, role: string[]): boolean => {
  return (
    idTokenClaims &&
    Array.isArray(idTokenClaims[CLAIMS.ID_TOKEN.ROLES]) &&
    idTokenClaims[CLAIMS.ID_TOKEN.ROLES].some((c: string) => role.includes(c))
  );
};

export const generateStaticTierInfo = (
  account: Account,
  theme: Theme | null | undefined,
  idToken: Record<string, any> | null | undefined,
) => {
  const now = new Date().getTime();
  const base = {
    isStarter: theme?.themeInternal?.tier === 'starter',
    isEssentials: theme?.themeInternal?.tier === 'essentials',
    isPro: theme?.themeInternal?.tier === 'pro',
    isEnterprise: theme?.themeInternal?.tier === 'enterprise',
    isMinEssentials: ['essentials', 'pro', 'enterprise'].includes(theme?.themeInternal?.tier || ''),
    isMinPro: ['pro', 'enterprise'].includes(theme?.themeInternal?.tier || ''),
    isTrial:
      theme?.themeInternal?.trialEndDate &&
      new Date(theme?.themeInternal?.trialEndDate).getTime() > now &&
      theme?.themeInternal?.trialStatus === TRIALSTATUS.CURRENT,
    isTrialFinished:
      theme?.themeInternal?.trialEndDate &&
      new Date(theme?.themeInternal?.trialEndDate).getTime() <= now &&
      theme?.themeInternal?.trialStatus === TRIALSTATUS.FINISHED,
    isThemeAdmin: idToken ? hasRole(idToken, [ROLES.THEME_ADMIN]) : false,
    isUnitAdmin: idToken ? hasRole(idToken, [ROLES.UNIT_ADMIN]) : false,
    isAnyAdmin: idToken ? hasRole(idToken, [ROLES.THEME_ADMIN, ROLES.UNIT_ADMIN]) : false,
    isMainAccount: theme?.themeInternal?.mainAccountId === account.id,
    wasCreatedInTheme: typeof account.attachedThemeId === 'number',
    tier: theme?.themeInternal?.tier,
    roles: (idToken?.[CLAIMS.ID_TOKEN.ROLES] || []) as string[],
  };

  const derivedProps = {
    canStartTrial: !base.isTrialFinished && !base.wasCreatedInTheme,
  };
  return {
    ...base,
    ...derivedProps,
  };
};
export type StaticTierInfo = ReturnType<typeof generateStaticTierInfo>;

export const extractNameFromEmail = (email: string) => {
  if (!email) return { firstName: '', lastName: '' };
  const matches = email.match(/([a-z]+)\.([a-z]+)@/);
  if (matches && matches[1] && matches[1].length > 1 && matches[2] && matches[2].length > 1) {
    const firstName = matches[1].charAt(0).toUpperCase() + matches[1].substring(1);
    const lastName = matches[2].charAt(0).toUpperCase() + matches[2].substring(1);
    return { firstName, lastName };
  }
  return { firstName: '', lastName: '' };
};

export const isLeadCustomFieldOptionType = (fieldType?: LeadCustomFieldType | null) => {
  return (
    fieldType === LEAD_CUSTOM_FIELDS.DROPDOWN ||
    fieldType === LEAD_CUSTOM_FIELDS.CHECKBOX ||
    fieldType === LEAD_CUSTOM_FIELDS.RADIO ||
    fieldType === LEAD_CUSTOM_FIELDS.MULTI_SELECT
  );
};

const domainRegex = /^(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}$/;
export const isValidDomain = (domain?: string) => {
  if (!domain || domain.length > 253) return false;
  return domainRegex.test(domain);
};

export const sortObjectKeys = (obj: Record<string, any>) => {
  return Object.keys(obj)
    .sort()
    .reduce(
      (acc, key) => {
        acc[key] = obj[key];
        return acc;
      },
      {} as Record<string, any>,
    );
};

export const isLinkedInProfileUrl = (url: string) => {
  return urlRegexp.test(url) && url.includes('linkedin.com');
};

export const getMissingFieldsCombinationsForLeadEnrichment = (lead: Partial<LeadAPI>) => {
  const validCombinations = [
    ['firstName', 'lastName', 'company'],
    ['firstName', 'lastName', 'website'],
    ['email'],
  ];

  const validCombination = validCombinations.find(combination =>
    combination.every(field => lead[field as keyof LeadAPI]),
  );

  if (validCombination) {
    return [];
  }
  return validCombinations;
};

export const isLeadEnrichable = (lead: Partial<LeadAPI>) => {
  const missingFieldsCombinations = getMissingFieldsCombinationsForLeadEnrichment(lead);
  return missingFieldsCombinations.length === 0;
};

export const isLeadScrapable = (lead: Partial<LeadAPI>) => Boolean(lead.linkedInLink);

// transposes the first two levels of an object
export const transposeObject = <T extends string, U extends string>(obj: {
  [l1 in U]: { [l2 in T]: unknown };
}): { [l2 in T]: { [l1 in U]: unknown } } => {
  return (Object.keys(obj) as U[]).reduce(
    (prev, cur) => {
      const keys = Object.keys(obj[cur]) as T[];
      keys.forEach(key => (prev[key] = { ...(prev[key] || {}), [key]: obj[cur][key] }));
      return prev;
    },
    {} as { [l2 in T]: { [l1 in U]: unknown } },
  );
};

export const getLinkWithHttps = (link: string) => {
  return link.startsWith('https') ? link : 'https://' + link;
};

export const fromEntries = <T>(entries: [string, T][]) => {
  const obj: Record<string, T> = {};
  for (const [key, value] of entries) {
    obj[key] = value;
  }
  return obj;
};

export const filterEmptyProperties = <T extends object>(data: T) => {
  return fromEntries(Object.entries(data).filter(([_, value]) => typeof value !== 'undefined'));
};

export const isValidDate = (date: unknown) => {
  return date instanceof Date && !isNaN(date.getTime());
};
export const isValidHttpUrl = (urlString: string): boolean => {
  let url: URL;
  try {
    url = new URL(urlString);
  } catch (_) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

export const isValidJson = (jsonString: string): boolean => {
  try {
    JSON.parse(jsonString);
    return true;
  } catch (_) {
    return false;
  }
};

export const safeJsonParse = (jsonString: string) => {
  try {
    return JSON.parse(jsonString);
  } catch (_) {
    return null;
  }
};

export const isDarkHex = (hex: string): boolean => {
  if (!hex?.startsWith('#')) return false;
  if (hex?.indexOf('#') === 0) {
    hex = hex.slice(1);
  }
  if (hex.length === 3) {
    hex = hex[0] ?? '' + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    return false;
  }
  const r = parseInt(hex.slice(0, 2), 16),
    g = parseInt(hex.slice(2, 4), 16),
    b = parseInt(hex.slice(4, 6), 16);

  return r * 0.299 + g * 0.587 + b * 0.114 < 186;
};
